import { Injectable } from '@angular/core';
import { Messaging, deleteToken, getToken, onMessage } from '@angular/fire/messaging';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MessageService } from 'primeng/api';
import { BannerService } from './amin-content/banner.service';

@Injectable({
  providedIn: 'root'
})
export class FcmService {
  constructor(
    private messaging: Messaging,
    private http: HttpClient,
    private messageService: MessageService,
    private bannerService: BannerService
  ) {}

  /** 1️⃣ Request permission */
  requestPermission(): Promise<NotificationPermission> {
    return Notification.requestPermission();
  }

  /** 2️⃣ Init FCM token */
  async initFcmToken(): Promise<void> {
    const permission = await this.requestPermission();

    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    const swReg = await navigator.serviceWorker.register('firebase-messaging-sw.js');

    await navigator.serviceWorker.ready;

    const token = await getToken(this.messaging, {
      vapidKey: environment.FCM_VAPID_KEY,
      serviceWorkerRegistration: swReg
    });

    if (token) {
      const savedToken = localStorage.getItem('fcm_token');
      if (token !== savedToken) {
        this.registerToken(token, this.getDeviceId()).subscribe({
          next: () => {
            localStorage.setItem('fcm_token', token);
          }
        });
      }
    }
  }

  /** 3️⃣ Listen foreground message */
  listenMessage(): Observable<any> {
    return new Observable(sub =>
      onMessage(this.messaging, msg => {
        sub.next(msg);
      })
    );
  }

  /** 4️⃣ Register token to backend */
  registerToken(token: string, deviceId?: string) {
    const id = deviceId || this.getDeviceId();
    return this.http.post(`${environment.API_URL}/notification/notifications/tokens/register`, { token, deviceId: id });
  }

  unregisterToken(token: string, deviceId?: string) {
    const userRaw = localStorage.getItem('user');
    const userId = userRaw ? JSON.parse(userRaw)?.id : '';
    const id = deviceId || this.getDeviceId();
    const body = {
      device_ids: [id],
      user_ids: userId ? [userId] : [],
      revoked_tokens: [token],
      is_revoke_all: true
    };

    return this.http.delete(`${environment.API_URL}/notification/notifications/tokens/firebase`, { body });
  }

  /** 5️⃣ Simple deviceId */
  private getDeviceId(): string {
    let id = localStorage.getItem('device_id');
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem('device_id', id);
    }
    return id;
  }

  async deleteFCMToken() {
    await deleteToken(this.messaging)
  }
}
